import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loading, toast } from "@foris/avocado-suite";
import {
  ContextEditLabel,
  useContextEditLabel,
} from "@dimensions/group/EditLabels/ContextEditLabels";
import { CubeQuery, Label } from "@models/ISchema";
import { emitEvent } from "@common/hooks/useListenEvent";
import { ContextApp } from "@config/Context/contextApp";
import useUpdateGroupLabel from "@dimensions/group/hooks/useUpdateGroupLabel";
import useContextUrl from "@common/hooks/useContextUrl";
import TagsDrawer from "@dimensions/common/tags-drawer";
import { useLazyQuery } from "@apollo/client";
import { labelsByGroupId } from "@dimensions/group/graphql/groupLabels.query";

interface GroupTagsDrawerProps {
  isOpen?: boolean;
  labels?: Label[];
  groupId?: string;
  modalityCode?: string;
  shouldFetchLabels?: boolean;
  onClose?: () => void;
}

const GroupTagsDrawer = ({
  isOpen = true,
  labels,
  groupId,
  modalityCode,
  shouldFetchLabels = false,
  onClose,
}: GroupTagsDrawerProps) => {
  const { t } = useTranslation();
  const { scenario, origin } = useContextUrl();
  const { user } = useContext(ContextApp);
  const contextEdit = useContextEditLabel();
  const [, updateGroupLabels] = useUpdateGroupLabel(groupId, scenario, origin);
  const [isLoading, setIsLoading] = useState(false);

  const [loadGroupLabels, { data, loading: isLoadingLabels }] = useLazyQuery<{ cube: CubeQuery }>(
    labelsByGroupId,
    {
      variables: {
        scenarioId: scenario,
        originId: origin,
        filter: {
          fields: {
            id: {
              eq: groupId,
            },
          },
        },
      },
    },
  );

  const queryLabels =
    isLoadingLabels || !isOpen ? [] : (data?.cube.groupsManagerSearch?.items[0]?.labels ?? []);

  useEffect(() => {
    if (shouldFetchLabels && groupId && isOpen && !isLoadingLabels) {
      loadGroupLabels();
    }
  }, [shouldFetchLabels, isOpen]);

  const handleUpdateLabels = () => {
    setIsLoading(true);
    updateGroupLabels(
      contextEdit.labels,
      () => {
        setIsLoading(false);
        onClose?.();
        emitEvent("on:refresh", { target: "group-calendar" });
        toast.success(t("edit-tag-manually.success-message"), { autoClose: 3000 });
      },
      error => {
        setIsLoading(false);
        console.error("Error updating labels", error);
      },
    );
  };

  const isLabelDisabled = useCallback(
    (label: Label) => modalityCode !== "05" && label?.code === "MT21",
    [modalityCode],
  );

  return (
    <ContextEditLabel.Provider value={contextEdit}>
      {isLoading && <Loading />}
      <TagsDrawer
        isOpen={isOpen}
        labels={shouldFetchLabels ? queryLabels : labels}
        canEdit={user?.permissions?.update}
        isLoadingLabels={isLoadingLabels}
        onSaveLabels={handleUpdateLabels}
        isLabelDisabled={isLabelDisabled}
        onClose={onClose}
      />
    </ContextEditLabel.Provider>
  );
};

export default GroupTagsDrawer;
